import { useId } from "react";
import { Link } from "react-router-dom";
import type { MailEvidence, PurchaseClaim, PurchaseClaimDelivery } from "./creditReview.js";
import { formatDateTime, validTimestamp } from "./format.js";
import { purchaseBookingLink, safePositiveId } from "./purchaseBookingLink.js";
import { mailStatusText, purchaseClaimState } from "./purchaseClaimState.js";
import "./purchaseClaims.css";

export function ClaimTime({ value, seconds = false }: { value: string | null | undefined; seconds?: boolean }) {
  return validTimestamp(value)
    ? <time dateTime={value} title={formatDateTime(value, seconds, true)}>{formatDateTime(value, seconds)}</time>
    : <>Unknown</>;
}

function MailDiagnostics({ label, mail }: { label: string; mail: MailEvidence | null | undefined }) {
  return <section aria-label={`${label} diagnostics`}>
    <h4>{label}</h4>
    {!mail ? <p>No email record</p> : <dl className="purchase-claim-facts">
      <dt>Status</dt><dd>{mailStatusText(mail)} (<code>{mail.status}</code>)</dd>
      <dt>Attempts</dt><dd>{mail.attempts}</dd>
      <dt>Sent to provider</dt><dd><ClaimTime value={mail.sentAt} seconds /></dd>
      <dt>Last status change</dt><dd><ClaimTime value={mail.updatedAt} seconds /></dd>
      <dt>Provider reference recorded</dt><dd>{mail.hasProviderId ? "Yes" : "No"}</dd>
      <dt>Last error</dt><dd>{mail.lastError || "None recorded"}</dd>
    </dl>}
  </section>;
}

export function PendingPurchaseClaimCard({ block, delivery, busy, resendPending = false, onResend, onAssign, onRefresh, now = Date.now() }: {
  block: PurchaseClaim;
  delivery: PurchaseClaimDelivery | undefined;
  busy: boolean;
  resendPending?: boolean;
  onResend: () => void;
  onAssign: () => void;
  onRefresh: () => void;
  now?: number;
}) {
  const { purchase, email, nextStep } = purchaseClaimState(block, delivery, now);
  const evidence = delivery?.paymentId === block.amelia_payment_id ? delivery : undefined;
  const claim = evidence?.claim;
  const bookingLink = purchaseBookingLink(block.amelia_purchase_booking_id, block.amelia_purchase_appointment_id);
  const reasonId = useId();
  const raw = (value: string | null | undefined) => <code>{value || "Unknown"}</code>;
  const id = (value: number | null | undefined) => safePositiveId(value) ? <code>{value}</code> : <>Unknown</>;
  const actionClass = (action: string) => nextStep.primary === action ? "action-primary" : "quiet";
  return <article className="slot purchase-claim-card" aria-label={`Paid purchase${safePositiveId(block.amelia_purchase_appointment_id) ? `, Amelia appointment ${block.amelia_purchase_appointment_id}` : ""}`}>
    <div className="purchase-claim-summary">
      <h4 className="purchase-claim-paid">Paid <ClaimTime value={block.paid_at} /></h4>
      {purchase.age && <p className="small muted purchase-claim-age">{purchase.age}</p>}
      <p><strong>{block.credits_purchased} lesson credits</strong> · Awaiting learner, not yet bookable.</p>
      <p className="small"><strong>Learner:</strong> {block.claim_purchase_student_name || "Learner not identified"}<br />
        <strong>Payer:</strong> {block.claim_payer_email || "No email recorded"}</p>
      <p className={`purchase-claim-next purchase-claim-next--${nextStep.tone}`}>{nextStep.text}</p>
      <p className="small muted">{email.line}</p>
    </div>
    <div className="purchase-claim-actions">
      {nextStep.primary === "reviews" && <a className="action-primary" href="#credit-review-items">Open credit reviews</a>}
      {bookingLink && <Link className={actionClass("booking")} to={bookingLink}>Open original booking</Link>}
      {nextStep.canAssign && <button className={actionClass("assign")} type="button" disabled={busy} onClick={onAssign}>Assign purchase</button>}
      {nextStep.resend !== "hidden" && <button className={actionClass("resend")} type="button" disabled={busy || resendPending || nextStep.resend === "disabled"}
        aria-describedby={nextStep.resendReason ? reasonId : undefined} onClick={onResend}>
        {block.claim_payer_email ? "Resend invitation" : "Check contact and send invitation"}
      </button>}
      {nextStep.primary === "refresh" && <button type="button" className="quiet" disabled={busy} onClick={onRefresh}>Refresh</button>}
      {nextStep.resendReason && <p id={reasonId} className="small muted">{nextStep.resendReason}</p>}
    </div>
    <details className="purchase-claim-details">
      <summary>Purchase details</summary>
      <dl className="purchase-claim-facts">
        <dt>Amelia appointment</dt><dd>{id(block.amelia_purchase_appointment_id)}</dd>
        <dt>Amelia booking</dt><dd>{id(block.amelia_purchase_booking_id)}</dd>
        <dt>Amelia payment</dt><dd>{id(block.amelia_payment_id)}</dd>
        <dt>Amount paid</dt><dd>{block.payment_amount ?? "Unknown"} {block.payment_currency ?? ""}</dd>
        <dt>Imported</dt><dd><ClaimTime value={block.created_at} seconds /></dd>
        <dt>Hold expiry</dt><dd><ClaimTime value={block.expires_at} seconds /></dd>
        <dt>Invite attempts</dt><dd>{block.claim_invite_attempts}</dd>
        <dt>Next worker attempt</dt><dd><ClaimTime value={block.claim_invite_next_at} seconds /></dd>
        <dt>Invitation status</dt><dd>{raw(block.claim_invite_status)}</dd>
        <dt>Invitation error</dt><dd>{raw(block.claim_invite_error)}</dd>
        <dt>Recovery status</dt><dd>{raw(block.claim_recovery_status)}</dd>
        <dt>Recovery error</dt><dd>{raw(block.claim_recovery_error)}</dd>
        <dt>Preference status</dt><dd>{raw(block.claim_preference_status)}</dd>
        <dt>Preference reason</dt><dd>{raw(block.claim_preference_reason)}</dd>
        <dt>Preference checked</dt><dd><ClaimTime value={block.claim_preference_checked_at} seconds /></dd>
        <dt>Reversal hold</dt><dd><ClaimTime value={block.payment_reversal_hold_at} seconds /></dd>
        <dt>Reversal cleared</dt><dd><ClaimTime value={block.payment_reversal_cleared_at} seconds /></dd>
      </dl>
      {evidence === undefined ? <p>Email status unavailable</p> : !claim ? <p>No Hub claim record</p> : <>
        <dl className="purchase-claim-facts">
          <dt>Hub claim created</dt><dd><ClaimTime value={claim.createdAt} seconds /></dd>
          <dt>Payer email verified</dt><dd><ClaimTime value={claim.verifiedAt} seconds /></dd>
          <dt>Learner chosen</dt><dd><ClaimTime value={claim.selectedAt} seconds /></dd>
          <dt>Delivery started</dt><dd><ClaimTime value={claim.deliveryStartedAt} seconds /></dd>
          <dt>Invitation created</dt><dd><ClaimTime value={claim.invitation?.createdAt} seconds /></dd>
          <dt>Invitation expires</dt><dd><ClaimTime value={claim.invitation?.expiresAt} seconds /></dd>
          <dt>Invitation enqueued</dt><dd><ClaimTime value={claim.invitation?.mailQueuedAt} seconds /></dd>
          <dt>Invitation accepted</dt><dd><ClaimTime value={claim.invitation?.acceptedAt} seconds /></dd>
          <dt>Invitation revoked</dt><dd><ClaimTime value={claim.invitation?.revokedAt} seconds /></dd>
        </dl>
        <MailDiagnostics label="Invitation email" mail={claim.invitationMail} />
        <MailDiagnostics label="Choose-learner email" mail={claim.actionMail} />
      </>}
    </details>
  </article>;
}
